import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/atoms';
import { FormField } from '../../components/molecues/FormField';
import { DeleteConfirmationModal, PreviewImagesSection } from '../../components/organisms';
import { useMovieActions } from '../../hooks/useMovies';
import { useCastActions, useCastAssignment } from '../../hooks/useCasts';
import { getLanguageService, getGenreService } from '../../service/ServiceRegistry';
import type { Language } from '../../types/api/language';
import type { Genre } from '../../types/api/genre';
import type {
  UpdateMovieRequest,
  MovieDetailsResponse,
  VideoVariant,
  UploadedPartStatus,
} from '../../types/api/movie';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../../components/atoms';
import { resolveAssetUrl } from '../../utils/url';
import { formatDateTimeForDisplay } from '../../types/api/movie';
import { DurationHmsField } from '../../components/molecues/DurationHmsField';
import { secondsToRuntimeMinutes } from '../../utils/durationInput';
import {
  clearUploadSession,
  getUploadSession,
  saveUploadSession,
  type UploadSession,
} from '../../utils/uploadSessionStorage';
import { CastAssignmentModal } from '../../components/organisms/CastAssignmentModal';
import type {
  PreviewImageMetadataRequest,
  PreviewImageResponse,
} from '../../types/api/movie';

/**
 * MovieEditPage Component
 * 
 * Separate page for editing movies
 * Features:
 * - Form with all movie fields
 * - Load existing movie data
 * - Language dropdown selection
 * - Genre multi-select
 * - Validation
 * - Error handling
 */

const DEFAULT_VIDEO_CHUNK_SIZE = 8 * 1024 * 1024; // 8MB per chunk
const VIDEO_MIN_PART_SIZE = 5 * 1024 * 1024; // Minimum part size (except last part)

const VIDEO_QUALITY_OPTIONS = [
  { value: 'ULTRA_HD_4K', label: 'Ultra HD (4K)' },
  { value: 'FULL_HD_1080P', label: 'Full HD (1080p)' },
  { value: 'HD_720P', label: 'HD (720p)' },
  { value: 'SD_480P', label: 'SD (480p)' },
  { value: 'SD_360P', label: 'SD (360p)' },
  { value: 'SD_240P', label: 'SD (240p)' },
];

const VIDEO_TYPE_OPTIONS = [
  { value: 'FULL_MOVIE', label: 'Full Movie' },
  { value: 'TRAILER', label: 'Trailer' },
  { value: 'TEASER', label: 'Teaser' },
  { value: 'CLIP', label: 'Clip' },
  { value: 'EXTRA', label: 'Extra' },
];

const getAdaptiveChunkSize = (fileSize: number): number => {
  const sizeInGB = fileSize / (1024 * 1024 * 1024);

  if (sizeInGB >= 20) {
    return 256 * 1024 * 1024; // 256 MB
  }
  if (sizeInGB >= 10) {
    return 192 * 1024 * 1024; // 192 MB
  }
  if (sizeInGB >= 5) {
    return 128 * 1024 * 1024; // 128 MB
  }
  if (sizeInGB >= 2) {
    return 96 * 1024 * 1024; // 96 MB
  }
  if (sizeInGB >= 1) {
    return 64 * 1024 * 1024; // 64 MB
  }
  if (sizeInGB >= 0.5) {
    return 32 * 1024 * 1024; // 32 MB
  }
  if (sizeInGB >= 0.25) {
    return 16 * 1024 * 1024; // 16 MB
  }

  return DEFAULT_VIDEO_CHUNK_SIZE;
};

export const MovieEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { movieId } = useParams<{ movieId: string }>();
  const {
    updateMovie,
    getMovie,
    initiateVideoUpload,
    completeVideoUpload,
    deleteVideo,
    getMultipartUploadStatus,
    regenerateMultipartPartUrls,
    uploadPreviewImages,
    updatePreviewImageMetadata,
    updatePreviewImageWithFile,
    updateMovieThumbnail,
  } = useMovieActions();
  const { searchCasts } = useCastActions();
  const languageService = getLanguageService();
  const genreService = getGenreService();

  const [movieDetails, setMovieDetails] = useState<MovieDetailsResponse | null>(null);
  const [formData, setFormData] = useState<UpdateMovieRequest>({
    title: '',
    description: '',
    languageId: '',
    director: '',
    runtime: 120,
    priority: undefined,
    releaseDate: '',
    initialGenreIds: [],
    primaryGenreId: '',
    status: 'ACTIVE',
    accessLevel: 'FREE',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Total seconds from H:M:S; API receives `runtime` as rounded whole minutes. */
  const [runtimeTotalSeconds, setRuntimeTotalSeconds] = useState(120 * 60);
  
  // Options for dropdowns
  const [languages, setLanguages] = useState<Language[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);

  // Video upload state
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoQuality, setVideoQuality] = useState<string>('FULL_HD_1080P');
  const [videoType, setVideoType] = useState<string>('FULL_MOVIE');
  const [makeDefaultVideo, setMakeDefaultVideo] = useState<boolean>(true);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'initiating' | 'uploading' | 'completing' | 'completed'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uploadSessionRef = useRef<{ key: string; uploadId: string } | null>(null);

  // Video delete state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVideoVariant, setSelectedVideoVariant] = useState<VideoVariant | null>(null);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);

  const [isCastModalOpen, setIsCastModalOpen] = useState(false);
  const {
    assignCast,
    removeCast,
    updateRole,
    getCasts,
  } = useCastAssignment('movie', movieId || '');

  const [isPreviewImagesBusy, setIsPreviewImagesBusy] = useState(false);

  // Thumbnail replacement state
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [isUpdatingThumbnail, setIsUpdatingThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);

  // Load movie data
  useEffect(() => {
    if (!movieId) {
      toast.error('Movie ID is required');
      navigate('/pages/movies');
      return;
    }

    const loadMovie = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const [movieDetailsData, langsResponse, genresResponse] = await Promise.all([
          getMovie(movieId),
          languageService.getLanguages({ page: 1, size: 100 }),
          genreService.getPublicGenres({ page: 1, size: 200 }),
        ]);

        setMovieDetails(movieDetailsData);
        setLoadError(null);
        
        // Extract movie data and genres
        const movie = movieDetailsData.movie;
        const genreIds = movieDetailsData.genres.map((g) => g.genreId);
        const primaryGenreId = movieDetailsData.primaryGenre?.genreId || '';

        setFormData({
          title: movie.title,
          description: movie.description,
          languageId: movie.languageId,
          director: movie.director,
          runtime: movie.runtime,
          priority: movie.priority ?? undefined,
          releaseDate: movie.releaseDate,
          status: movie.status,
          accessLevel: movie.accessLevel,
          initialGenreIds: genreIds,
          primaryGenreId: primaryGenreId,
        });
        const minutes = movie.runtime && movie.runtime > 0 ? movie.runtime : 120;
        setRuntimeTotalSeconds(minutes * 60);
        setLanguages(langsResponse.items);
        setGenres(genresResponse.items);
        const hasDefaultVariant = movieDetailsData.videoVariants?.some(
          (variant) => variant?.isDefault
        );
        setMakeDefaultVideo(!hasDefaultVariant);
      } catch (error: any) {
        // Don't show error if request was cancelled (e.g., by request deduplication)
        // Axios cancelled errors have specific properties or messages
        const isCancelled = 
          error?.message?.includes('cancel') || 
          error?.message?.includes('superseded') ||
          error?.message?.includes('Cancel') ||
          error?.error?.code === 'CANCEL' ||
          (error?.name === 'Cancel' || error?.name === 'CanceledError');
        
        if (isCancelled) {
          // Request was cancelled, likely due to request deduplication
          // This is normal in React StrictMode, so we don't show an error
          // The other request should handle the response
          return;
        }
        
        console.error('Failed to load movie:', error);
        // Don't navigate immediately - allow retry mechanism to work
        // Check if it's a retryable error (network/server error)
        const isRetryableError = 
          error?.error?.type === 'NETWORK_ERROR' || 
          error?.error?.type === 'SERVER_ERROR' ||
          error?.error?.code?.startsWith('5') ||
          error?.error?.code?.startsWith('HTTP_5') ||
          error?.error?.code === 'TIMEOUT';
        
        if (isRetryableError) {
          // For retryable errors, show error but don't navigate
          // The HttpClient retry logic will handle retries
          setLoadError(error.message || 'Failed to load movie. Please try again.');
          // toast.error(error.message || 'Failed to load movie. Retrying...');
        } else {
          // For non-retryable errors (404, 403, etc.), navigate away
          setLoadError(error.message || 'Failed to load movie');
          // toast.error(error.message || 'Failed to load movie');
          // Only navigate for non-retryable errors
          if (error?.error?.type === 'NOT_FOUND' || error?.error?.code === 'HTTP_404') {
            navigate('/pages/movies');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadMovie();
  }, [movieId, navigate, getMovie, languageService, genreService]);

  const handleChange = (field: keyof UpdateMovieRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleGenreToggle = (genreId: string) => {
    setFormData((prev) => {
      const currentIds = prev.initialGenreIds || [];
      const exists = currentIds.includes(genreId);
      const updatedIds = exists
        ? currentIds.filter((id) => id !== genreId)
        : [...currentIds, genreId];

      const updatedPrimary =
        exists && prev.primaryGenreId === genreId ? '' : prev.primaryGenreId;

      return {
        ...prev,
        initialGenreIds: updatedIds,
        primaryGenreId: updatedPrimary,
      };
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.initialGenreIds;
      delete newErrors.primaryGenreId;
      return newErrors;
    });
  };

  const handlePrimaryGenreChange = (genreId: string) => {
    handleChange('primaryGenreId', genreId);
  };

  const normaliseEtag = (raw: string | null): string => {
    if (!raw) {
      throw new Error('Missing ETag value');
    }
    return raw.replace(/^W\//, '').replace(/^"+|"+$/g, '');
  };

  const buildUploadedPartMap = (uploadedParts: UploadedPartStatus[] | null | undefined) => {
    const map = new Map<number, { eTag: string; size: number }>();
    if (!uploadedParts || uploadedParts.length === 0) {
      return map;
    }

    uploadedParts.forEach((part) => {
      try {
        const eTag = normaliseEtag(part.eTag);
        map.set(part.partNumber, {
          eTag,
          size: part.size ?? 0,
        });
      } catch (error) {
        console.error('Failed to normalise ETag from status response:', error);
      }
    });

    return map;
  };

  const getMissingPartNumbers = (
    totalPartCount: number,
    uploadedPartMap: Map<number, { eTag: string; size: number }>
  ): number[] => {
    const missing: number[] = [];
    for (let partNumber = 1; partNumber <= totalPartCount; partNumber++) {
      if (!uploadedPartMap.has(partNumber)) {
        missing.push(partNumber);
      }
    }
    return missing;
  };

  const uploadPartWithRetry = async (
    url: string,
    chunk: Blob,
    partNumber: number,
    maxRetries: number = 3
  ): Promise<string> => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          body: chunk,
        });

        if (!response.ok) {
          // Treat 403 as potentially expired URL so caller can regenerate
          if (response.status === 403) {
            const error = new Error('URL_EXPIRED');
            (error as any).status = response.status;
            throw error;
          }

          throw new Error(
            `Upload failed for part ${partNumber} (status ${response.status})`
          );
        }

        const rawETag = response.headers.get('ETag') || response.headers.get('etag');
        const eTag = normaliseEtag(rawETag);
        return eTag;
      } catch (error: any) {
        lastError = error;

        // If URL is expired, let caller regenerate a fresh URL
        if (error?.message === 'URL_EXPIRED') {
          throw error;
        }

        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
          console.warn(
            `Part ${partNumber} attempt ${attempt} failed, retrying in ${delay}ms...`,
            error
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error(`Part ${partNumber} failed after ${maxRetries} attempts`);
  };

  const resetVideoUploadState = () => {
    setVideoFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setUploadError(null);
    uploadSessionRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVideoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      resetVideoUploadState();
      return;
    }

    if (file.type && !file.type.startsWith('video/')) {
      setUploadError('Please select a valid video file');
      resetVideoUploadState();
      return;
    }

    if (file.size === 0) {
      setUploadError('Selected file is empty');
      resetVideoUploadState();
      return;
    }

    setVideoFile(file);
    setUploadError(null);
    setUploadProgress(0);
    setUploadStatus('idle');
  };

  /**
   * Get the file ID from a video variant
   * Prioritizes videoId, then id, then key
   */
  const getVideoFileId = (variant: VideoVariant): string | null => {
    console.log('variant', variant);
    return variant.videoId || variant.fileId || variant.key ||  null;
  };

  /**
   * Handle delete video button click
   */
  const handleDeleteVideo = (variant: VideoVariant) => {
    const fileId = getVideoFileId(variant);
    if (!fileId) {
      toast.error('Cannot delete video: Missing video identifier');
      return;
    }
    setSelectedVideoVariant(variant);
    setIsDeleteModalOpen(true);
  };

  /**
   * Handle video deletion confirmation
   */
  const handleDeleteVideoConfirm = async () => {
    if (!selectedVideoVariant || !movieId || !movieDetails) {
      return;
    }

    const fileId = getVideoFileId(selectedVideoVariant);
    if (!fileId) {
      toast.error('Cannot delete video: Missing video identifier');
      setIsDeleteModalOpen(false);
      setSelectedVideoVariant(null);
      return;
    }

    setIsDeletingVideo(true);
    try {
      await deleteVideo(movieDetails.movie.contentId, fileId);
      toast.success('Video deleted successfully');
      
      // Refresh movie details to update the video variants list
      const updatedDetails = await getMovie(movieId);
      setMovieDetails(updatedDetails);
      
      // Update default video checkbox state
      const hasDefaultVariant = updatedDetails.videoVariants?.some(
        (variant) => variant?.isDefault
      );
      setMakeDefaultVideo(!hasDefaultVariant);
      
      setIsDeleteModalOpen(false);
      setSelectedVideoVariant(null);
    } catch (error: any) {
      console.error('Failed to delete video:', error);
      toast.error(error.message || 'Failed to delete video');
    } finally {
      setIsDeletingVideo(false);
    }
  };

  const handleVideoUpload = async () => {
    if (!movieId) {
      toast.error('Movie ID is missing');
      return;
    }

    if (!videoFile) {
      setUploadError('Please select a video file to upload');
      return;
    }

    const fileSize = videoFile.size;
    const adaptiveChunkSize = Math.max(
      getAdaptiveChunkSize(fileSize),
      VIDEO_MIN_PART_SIZE
    );
    const estimatedPartCount = Math.max(1, Math.ceil(fileSize / adaptiveChunkSize));

    setIsUploadingVideo(true);
    setUploadStatus('initiating');
    setUploadProgress(0);
    setUploadError(null);

    let completedParts: { partNumber: number; eTag: string }[] = [];
    let sessionInfo: { key: string; uploadId: string } | null = null;
    let session: UploadSession | null = null;

    try {
      // Try to resume existing upload session if it matches the selected file
      session = getUploadSession(movieId) || null;
      const canResumeFromSession =
        session &&
        session.filename === videoFile.name &&
        session.fileSize === fileSize &&
        session.contentId === movieId;

      if (canResumeFromSession && session) {
        sessionInfo = {
          key: session.key,
          uploadId: session.uploadId,
        };
        uploadSessionRef.current = sessionInfo;
      } else {
        // Start a new multipart upload session
        const initiateResponse = await initiateVideoUpload(movieId, {
          filename: videoFile.name,
          contentType: videoFile.type || 'application/octet-stream',
          partCount: estimatedPartCount,
          quality: videoQuality,
          videoType,
          makeDefault: makeDefaultVideo,
        });

        sessionInfo = {
          key: initiateResponse.key,
          uploadId: initiateResponse.uploadId,
        };
        uploadSessionRef.current = sessionInfo;

        session = {
          contentId: movieId,
          key: initiateResponse.key,
          uploadId: initiateResponse.uploadId,
          partCount: estimatedPartCount,
          filename: videoFile.name,
          fileSize,
          chunkSize: adaptiveChunkSize,
          quality: videoQuality,
          videoType,
          makeDefault: makeDefaultVideo,
          contentType: videoFile.type || 'application/octet-stream',
          createdAt: Date.now(),
        };

        saveUploadSession(session);
      }

      if (!sessionInfo || !session) {
        throw new Error('Failed to initialise upload session');
      }

      setUploadStatus('uploading');

      // Check which parts are already uploaded (for resume scenarios)
      let uploadedBytes = 0;
      let uploadedPartMap = new Map<number, { eTag: string; size: number }>();
      let totalPartCount = session.partCount;

      try {
        const status = await getMultipartUploadStatus(
          movieId,
          sessionInfo.key,
          sessionInfo.uploadId
        );

        uploadedPartMap = buildUploadedPartMap(status.uploadedParts);
        if (status.partCount != null && status.partCount > 0) {
          totalPartCount = status.partCount;
        }

        status.uploadedParts?.forEach((part) => {
          uploadedBytes += part.size ?? 0;
        });
      } catch (statusError) {
        console.warn('Failed to fetch multipart upload status, assuming fresh upload', statusError);
        uploadedBytes = 0;
        uploadedPartMap = new Map();
        totalPartCount = session.partCount;
      }

      completedParts = Array.from(uploadedPartMap.entries()).map(
        ([partNumber, value]) => ({
          partNumber,
          eTag: value.eTag,
        })
      );

      // Determine which parts still need to be uploaded
      const missingPartNumbers = getMissingPartNumbers(totalPartCount, uploadedPartMap);

      if (missingPartNumbers.length === 0) {
        // All parts are already uploaded; just complete the multipart upload
        setUploadProgress(Math.min(100, Math.round((uploadedBytes / fileSize) * 100)));
      } else {
        // Regenerate presigned URLs for all missing parts
        const presignResponse = await regenerateMultipartPartUrls(movieId, {
          key: sessionInfo.key,
          uploadId: sessionInfo.uploadId,
          partNumbers: missingPartNumbers,
        });

        const partsToUpload = [...presignResponse.parts].sort(
          (a, b) => a.partNumber - b.partNumber
        );

        const baseChunkSize = session.chunkSize || adaptiveChunkSize;

        for (let index = 0; index < partsToUpload.length; index++) {
          const part = partsToUpload[index];

          if (uploadedBytes >= fileSize) {
            break;
          }

          const remainingBytes = fileSize - uploadedBytes;
          const partsRemaining = partsToUpload.length - index;
          const isLastPart = partsRemaining === 1;
          const minPartSize = part.minPartSize ?? VIDEO_MIN_PART_SIZE;
          const maxPartSize = part.maxPartSize ?? Math.max(baseChunkSize, minPartSize);

          // Calculate an appropriate chunk size ensuring non-final parts stay >= 5MB
          const bytesNeededForRemainingParts = isLastPart
            ? 0
            : Math.max(VIDEO_MIN_PART_SIZE, minPartSize) * (partsRemaining - 1);

          if (!isLastPart && remainingBytes - bytesNeededForRemainingParts < minPartSize) {
            throw new Error(
              `Not enough data remaining to satisfy minimum part size for part ${part.partNumber}`
            );
          }

          const chunkSize = isLastPart
            ? remainingBytes
            : Math.min(
                Math.max(
                  minPartSize,
                  Math.min(baseChunkSize, remainingBytes - bytesNeededForRemainingParts)
                ),
                maxPartSize,
                remainingBytes
              );

          const chunk = videoFile.slice(uploadedBytes, uploadedBytes + chunkSize);

          if (!isLastPart && chunk.size < minPartSize) {
            throw new Error(
              `Uploaded chunk ${part.partNumber} (${chunk.size} bytes) is smaller than the minimum ${minPartSize} bytes`
            );
          }

          try {
            const eTag = await uploadPartWithRetry(part.url, chunk, part.partNumber);
            completedParts.push({
              partNumber: part.partNumber,
              eTag,
            });
            uploadedBytes += chunk.size;
            setUploadProgress(
              Math.min(100, Math.round((uploadedBytes / fileSize) * 100))
            );
          } catch (error: any) {
            // If URL expired, regenerate a fresh URL for this specific part and retry once
            if (error?.message === 'URL_EXPIRED') {
              const singlePresign = await regenerateMultipartPartUrls(movieId, {
                key: sessionInfo.key,
                uploadId: sessionInfo.uploadId,
                partNumbers: [part.partNumber],
              });

              const refreshedPart = singlePresign.parts[0];
              const eTag = await uploadPartWithRetry(
                refreshedPart.url,
                chunk,
                refreshedPart.partNumber
              );

              completedParts.push({
                partNumber: refreshedPart.partNumber,
                eTag,
              });
              uploadedBytes += chunk.size;
              setUploadProgress(
                Math.min(100, Math.round((uploadedBytes / fileSize) * 100))
              );
            } else {
              throw error;
            }
          }
        }
      }

      if (completedParts.length === 0) {
        throw new Error('No video parts were uploaded');
      }

      // Ensure parts are ordered by partNumber for completion
      completedParts.sort((a, b) => a.partNumber - b.partNumber);

      setUploadStatus('completing');

      await completeVideoUpload(movieId, {
        key: sessionInfo.key,
        uploadId: sessionInfo.uploadId,
        parts: completedParts,
        quality: videoQuality,
        videoType,
        makeDefault: makeDefaultVideo,
        durationSeconds: null,
        bitrate: null,
        codec: null,
        container: null,
        width: null,
        height: null,
      });

      setUploadStatus('completed');
      setUploadProgress(100);
      toast.success('Video uploaded successfully');

      const updatedDetails = await getMovie(movieId);
      setMovieDetails(updatedDetails);
      const hasDefaultVariant = updatedDetails.videoVariants?.some(
        (variant) => variant?.isDefault
      );
      setMakeDefaultVideo(!hasDefaultVariant);

      clearUploadSession(movieId);
      resetVideoUploadState();
    } catch (error: any) {
      console.error('Video upload failed:', error);
      const message = error?.message || 'Failed to upload video';
      setUploadError(message);
      toast.error(message);
      // Do not abort the multipart upload here so that it can be resumed later.
    } finally {
      setIsUploadingVideo(false);
      setUploadStatus('idle');
      uploadSessionRef.current = null;
    }
  };


  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.languageId) {
      newErrors.languageId = 'Language is required';
    }
    if (!formData.director?.trim()) {
      newErrors.director = 'Director is required';
    }
    if (runtimeTotalSeconds <= 0) {
      newErrors.runtime = 'Runtime must be greater than zero';
    }
    if (!formData.releaseDate) {
      newErrors.releaseDate = 'Release date is required';
    }
    if (!formData.initialGenreIds || formData.initialGenreIds.length === 0) {
      newErrors.initialGenreIds = 'At least one genre is required';
    }
    if (!formData.primaryGenreId) {
      newErrors.primaryGenreId = 'Primary genre is required';
    }

    if (
      formData.priority !== undefined &&
      (!Number.isInteger(formData.priority) || formData.priority < 0)
    ) {
      newErrors.priority = 'Priority must be an integer >= 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !movieId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateMovie(movieId, {
        ...formData,
        runtime: secondsToRuntimeMinutes(runtimeTotalSeconds),
      });
      toast.success('Movie updated successfully');
      navigate('/pages/movies');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update movie');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!movieDetails || !movieDetails.movie) {
    return (
      <div className="text-center py-12">
        {loadError ? (
          <>
            <p className="text-red-600 mb-4">{loadError}</p>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="primary"
                onClick={() => {
                  setLoadError(null);
                  setIsLoading(true);
                  Promise.all([
                    getMovie(movieId!),
                    languageService.getLanguages({ page: 1, size: 100 }),
                    genreService.getPublicGenres({ page: 1, size: 200 }),
                  ])
                    .then(([movieDetailsData, langsResponse, genresResponse]) => {
                      setMovieDetails(movieDetailsData);
                      setLoadError(null);
                      
                      // Extract movie data and genres
                      const movie = movieDetailsData.movie;
                      const genreIds = movieDetailsData.genres.map((g) => g.genreId);
                      const primaryGenreId = movieDetailsData.primaryGenre?.genreId || '';

                      setFormData({
                        title: movie.title,
                        description: movie.description,
                        languageId: movie.languageId,
                        director: movie.director,
                        runtime: movie.runtime,
                        priority: movie.priority ?? undefined,
                        releaseDate: movie.releaseDate,
                        status: movie.status,
                        accessLevel: movie.accessLevel,
                        initialGenreIds: genreIds,
                        primaryGenreId: primaryGenreId,
                      });
                      const minutes = movie.runtime && movie.runtime > 0 ? movie.runtime : 120;
                      setRuntimeTotalSeconds(minutes * 60);
                      setLanguages(langsResponse.items);
                      setGenres(genresResponse.items);
                      const hasDefaultVariant = movieDetailsData.videoVariants?.some(
                        (variant) => variant?.isDefault
                      );
                      setMakeDefaultVideo(!hasDefaultVariant);
                      toast.success('Movie loaded successfully');
                    })
                    .catch((err: any) => {
                      setLoadError(err.message || 'Failed to load movie');
                      toast.error(err.message || 'Failed to load movie');
                    })
                    .finally(() => setIsLoading(false));
                }}
                className="mt-4"
              >
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pages/movies')}
                className="mt-4"
              >
                Back to Movies
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-600">Movie not found</p>
            <Button
              variant="outline"
              onClick={() => navigate('/pages/movies')}
              className="mt-4"
            >
              Back to Movies
            </Button>
          </>
        )}
      </div>
    );
  }

  const movie = movieDetails.movie;

  const handleFetchAssignedCasts = async () => {
    return await getCasts();
  };

  const handleSearchCasts = async (keyword: string) => {
    const page = 0;
    const size = 10;
    const pageData = await searchCasts(keyword, page, size);
    return pageData.items;
  };

  const handleUploadPreviewImages = async (files: File[]) => {
    if (!movieId) {
      toast.error('Movie ID is missing');
      return;
    }

    const existingCount = (movieDetails.previewImages || []).length;
    const maxTotal = 10;
    const maxPerUpload = 5;

    if (files.length === 0) {
      toast.error('Please select at least one image to upload');
      return;
    }

    if (files.length > maxPerUpload) {
      toast.error(`You can upload up to ${maxPerUpload} images per request`);
      return;
    }

    if (existingCount >= maxTotal) {
      toast.error('Maximum 10 preview images per movie reached');
      return;
    }

    if (existingCount + files.length > maxTotal) {
      toast.error(
        `You can upload only ${maxTotal - existingCount} more image${
          maxTotal - existingCount > 1 ? 's' : ''
        } for this movie`
      );
      return;
    }

    setIsPreviewImagesBusy(true);
    try {
      await uploadPreviewImages(movieId, files);
      const updatedDetails = await getMovie(movieId);
      setMovieDetails(updatedDetails);
      toast.success('Preview images uploaded successfully');
    } catch (error: any) {
      console.error('Failed to upload preview images for movie:', error);
      toast.error(error?.message || 'Failed to upload preview images');
    } finally {
      setIsPreviewImagesBusy(false);
    }
  };

  const handleUpdatePreviewImageMetadata = async (
    previewImageId: string,
    metadata: PreviewImageMetadataRequest
  ) => {
    if (!movieId) {
      toast.error('Movie ID is missing');
      return;
    }

    setIsPreviewImagesBusy(true);
    try {
      await updatePreviewImageMetadata(movieId, previewImageId, metadata);
      const updatedDetails = await getMovie(movieId);
      setMovieDetails(updatedDetails);
      toast.success('Preview image updated successfully');
    } catch (error: any) {
      console.error('Failed to update preview image metadata for movie:', error);
      toast.error(error?.message || 'Failed to update preview image');
    } finally {
      setIsPreviewImagesBusy(false);
    }
  };

  const handleReplacePreviewImage = async (
    previewImageId: string,
    metadata: PreviewImageMetadataRequest,
    file?: File
  ) => {
    if (!movieId) {
      toast.error('Movie ID is missing');
      return;
    }

    setIsPreviewImagesBusy(true);
    try {
      await updatePreviewImageWithFile(movieId, previewImageId, metadata, file);
      const updatedDetails = await getMovie(movieId);
      setMovieDetails(updatedDetails);
      toast.success('Preview image updated successfully');
    } catch (error: any) {
      console.error('Failed to replace preview image for movie:', error);
      toast.error(error?.message || 'Failed to update preview image');
    } finally {
      setIsPreviewImagesBusy(false);
    }
  };

  const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setThumbnailFile(null);
      setThumbnailPreview(null);
      setThumbnailError(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setThumbnailError('Please select an image file');
      setThumbnailFile(null);
      setThumbnailPreview(null);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setThumbnailError('File size must be less than 10MB');
      setThumbnailFile(null);
      setThumbnailPreview(null);
      return;
    }

    setThumbnailFile(file);
    setThumbnailError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleThumbnailUpdate = async () => {
    if (!movieId) {
      toast.error('Movie ID is missing');
      return;
    }
    if (!thumbnailFile) {
      setThumbnailError('Please select an image to upload');
      return;
    }

    setIsUpdatingThumbnail(true);
    try {
      const updated = await updateMovieThumbnail(movieId, thumbnailFile);
      // Update local details thumbnailUrl to avoid stale image
      setMovieDetails((prev) =>
        prev
          ? {
              ...prev,
              movie: {
                ...prev.movie,
                thumbnailUrl: updated.thumbnailUrl,
              },
            }
          : prev
      );
      toast.success('Thumbnail updated successfully');
      setThumbnailFile(null);
      setThumbnailPreview(null);
    } catch (error: any) {
      console.error('Failed to update movie thumbnail:', error);
      toast.error(error.message || 'Failed to update thumbnail');
    } finally {
      setIsUpdatingThumbnail(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Edit Movie</h1>
          <p className="text-gray-600 mt-1">Update movie information</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => setIsCastModalOpen(true)}
          >
            Manage Casts
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/pages/movies')}
          >
            Cancel
          </Button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-base text-left font-semibold text-gray-900 pb-2">
            Basic Information
          </h2>
          
          <FormField
            label="Title"
            value={formData.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="Enter movie title"
          />

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter movie description"
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.description && (
              <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Language <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.languageId || ''}
                onChange={(e) => handleChange('languageId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="">Select a language</option>
                {languages
                  .filter((lang) => lang.isActive)
                  .map((lang) => (
                    <option key={lang.languageId} value={lang.languageId}>
                      {lang.name} ({lang.nameMm})
                    </option>
                  ))}
              </select>
              {errors.languageId && (
                <p className="text-sm text-red-600 mt-1.5">{errors.languageId}</p>
              )}
            </div>

            <FormField
              label="Director"
              value={formData.director || ''}
              onChange={(e) => handleChange('director', e.target.value)}
              error={errors.director}
              required
              placeholder="Enter director name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <DurationHmsField
                label="Runtime"
                valueSeconds={runtimeTotalSeconds}
                onSecondsChange={(sec) => setRuntimeTotalSeconds(sec ?? 0)}
                required
                error={errors.runtime}
                helperText="Hours : minutes : seconds. Saved as whole minutes (rounded) in runtime."
                idPrefix="movie-edit-runtime"
              />
            </div>

            <FormField
              label="Priority"
              type="number"
              value={formData.priority != null ? formData.priority.toString() : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  handleChange('priority', undefined);
                  return;
                }
                const parsed = parseInt(raw, 10);
                handleChange('priority', Number.isNaN(parsed) ? undefined : parsed);
              }}
              error={errors.priority}
              min={0}
              placeholder="0"
            />

            <FormField
              label="Release Date"
              type="date"
              value={formData.releaseDate || ''}
              onChange={(e) => handleChange('releaseDate', e.target.value)}
              error={errors.releaseDate}
              required
            />

            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Status
              </label>
              <select
                value={formData.status || 'ACTIVE'}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Access Level
            </label>
            <select
              value={formData.accessLevel || 'FREE'}
              onChange={(e) => handleChange('accessLevel', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            >
              <option value="FREE">Free</option>
              <option value="PREMIUM">Premium</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
          </div>
        </div>

        {/* Genres */}
        <div className="space-y-4">
          <h2 className="text-base text-left pt-4 font-semibold text-gray-900 border-t border-dashed border-gray-600 pb-2">
            Genres
          </h2>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Select Genres <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {genres
                .filter((genre) => genre.isActive)
                .map((genre) => (
                  <label
                    key={genre.genreId}
                    className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.initialGenreIds?.includes(genre.genreId) || false}
                      onChange={() => handleGenreToggle(genre.genreId)}
                      className="w-4 h-4 text-[#EAB308] border-gray-300 rounded focus:ring-[#EAB308]"
                    />
                    <span className="text-sm text-gray-700">{genre.name}</span>
                  </label>
                ))}
            </div>
            {errors.initialGenreIds && (
              <p className="text-sm text-red-600 mt-1.5">{errors.initialGenreIds}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Primary Genre <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.primaryGenreId || ''}
              onChange={(e) => handlePrimaryGenreChange(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            >
              <option value="">Select primary genre</option>
              {genres
                .filter(
                  (genre) =>
                    genre.isActive && formData.initialGenreIds?.includes(genre.genreId)
                )
                .map((genre) => (
                  <option key={genre.genreId} value={genre.genreId}>
                    {genre.name}
                  </option>
                ))}
            </select>
            {errors.primaryGenreId && (
              <p className="text-sm text-red-600 mt-1.5">{errors.primaryGenreId}</p>
            )}
            {formData.initialGenreIds && formData.initialGenreIds.length > 0 && (
              <p className="text-sm text-gray-500 mt-1.5 text-left">
                Primary genre must be selected from the genres above.
              </p>
            )}
          </div>
        </div>

        {/* Video Upload */}
        <div className="space-y-4">
          <h2 className="text-base text-left font-semibold text-gray-900 border-t border-dashed border-gray-600 pt-4 pb-2">
            Video Upload
          </h2>
          {/* <div className='h-2'></div> */}
          <p className="text-sm text-blue-400 text-left mb-4">
            Upload a new video variant. Large files are automatically uploaded in secure chunks using multipart upload.
          </p>
          <div className='h-1'></div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-left text-gray-700">
                Video File <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoFileChange}
                disabled={isUploadingVideo}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              />
              <p className="text-xs text-gray-500">
                Supported: MP4, MOV, MKV, WEBM and other video formats. Minimum chunk size 5MB per part.
              </p>
              {videoFile && (
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <p className="font-medium truncate">{videoFile.name}</p>
                  <p>
                    Size:{' '}
                    {(videoFile.size / (1024 * 1024)).toFixed(2)}
                    {' '}MB
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-left text-gray-700 mb-3">
                    Quality <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={videoQuality}
                    onChange={(e) => setVideoQuality(e.target.value)}
                    disabled={isUploadingVideo}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
                  >
                    {VIDEO_QUALITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-left text-gray-700 mb-3">
                    Video Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={videoType}
                    onChange={(e) => setVideoType(e.target.value)}
                    disabled={isUploadingVideo}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
                  >
                    {VIDEO_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-[#EAB308] border-gray-300 rounded focus:ring-[#EAB308]"
                  checked={makeDefaultVideo}
                  onChange={(e) => setMakeDefaultVideo(e.target.checked)}
                  disabled={isUploadingVideo}
                />
                Set as default playback source
              </label>

              {uploadError && (
                <p className="text-sm text-red-600">{uploadError}</p>
              )}

              {(isUploadingVideo || uploadProgress > 0) && (
                <div className="space-y-2">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-2 bg-[#EAB308] transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    {isUploadingVideo
                      ? uploadStatus === 'uploading'
                        ? `Uploading... ${uploadProgress}%`
                        : uploadStatus === 'initiating'
                        ? 'Preparing upload session...'
                        : uploadStatus === 'completing'
                        ? 'Finalising upload...'
                        : 'Processing...'
                      : uploadProgress === 100
                      ? 'Upload completed'
                      : `Progress: ${uploadProgress}%`}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleVideoUpload}
                  disabled={!videoFile || isUploadingVideo}
                  isLoading={isUploadingVideo}
                >
                  Upload Video
                </Button>
                {videoFile && !isUploadingVideo && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetVideoUploadState}
                  >
                    Clear Selection
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Existing Video Variants */}
        {movieDetails.videoVariants && movieDetails.videoVariants.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Existing Video Variants
            </h2>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Quality
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Default
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Updated
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {movieDetails.videoVariants.map((variant: VideoVariant) => {
                    const variantKey =
                      variant.fileId ||
                      variant.videoId ||
                      variant.key ||
                      `${variant.videoType}-${variant.quality}`;

                    const statusClasses =
                      variant.status === 'READY'
                        ? 'bg-green-100 text-green-800'
                        : variant.status === 'PROCESSING' || variant.status === 'UPLOADING'
                        ? 'bg-yellow-100 text-yellow-800'
                        : variant.status === 'FAILED'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800';

                    return (
                      <tr key={variantKey}>
                        <td className="px-4 py-3 text-gray-800">
                          {variant.videoType || '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {variant.quality || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusClasses}`}>
                            {variant.status || 'UNKNOWN'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {variant.isDefault ? (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                              Default
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {formatDateTimeForDisplay(variant.updatedAt || variant.createdAt || null)}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteVideo(variant)}
                            disabled={isDeletingVideo}
                            ariaLabel={`Delete ${variant.videoType || 'video'} - ${variant.quality || 'unknown quality'}`}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Thumbnail */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 pb-2 text-left">
            Thumbnail
          </h2>

          <div className="flex flex-col md:flex-row gap-6">
            {/* Current thumbnail */}
            {movie.thumbnailUrl && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700 text-left">Current Thumbnail</p>
                <div className="relative">
                  <img
                    src={resolveAssetUrl(movie.thumbnailUrl)}
                    alt={movie.title}
                    className="w-48 h-64 object-cover rounded-lg border border-gray-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div
                    className="w-48 h-64 bg-gray-200 rounded-lg border border-gray-300 flex items-center justify-center"
                    style={{ display: 'none' }}
                  >
                    <div className="text-center">
                      <svg
                        className="w-12 h-12 text-gray-400 mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-xs text-gray-500">No Image</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Thumbnail replace */}
            <div className="flex-1 space-y-3">
              <p className="text-sm font-medium text-gray-700 text-left">
                Replace Thumbnail (optional)
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailFileChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              />
              {thumbnailError && (
                <p className="text-sm text-red-600">{thumbnailError}</p>
              )}
              <p className="text-xs text-gray-500 text-left">
                JPEG, PNG, or WebP. Max 10MB. Uses dedicated /thumbnail endpoint without
                touching other metadata.
              </p>
              {thumbnailPreview && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600 mb-1 text-left">New Thumbnail Preview</p>
                  <img
                    src={thumbnailPreview}
                    alt="New thumbnail preview"
                    className="w-32 h-44 object-cover rounded-lg border border-gray-300"
                  />
                </div>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleThumbnailUpdate}
                  disabled={!thumbnailFile || isUpdatingThumbnail}
                  isLoading={isUpdatingThumbnail}
                >
                  Update Thumbnail Only
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Images */}
        <PreviewImagesSection
          title="Preview Images"
          images={(movieDetails.previewImages as PreviewImageResponse[]) || []}
          maxPerUpload={5}
          maxTotal={10}
          isBusy={isPreviewImagesBusy}
          onUpload={handleUploadPreviewImages}
          onUpdateMetadata={handleUpdatePreviewImageMetadata}
          onReplaceImage={handleReplacePreviewImage}
        />

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/pages/movies')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            Update Movie
          </Button>
        </div>
      </form>

      {/* Cast Assignment Modal */}
      {movieId && (
        <CastAssignmentModal
          isOpen={isCastModalOpen}
          onClose={() => setIsCastModalOpen(false)}
          contentType="movie"
          contentId={movieId}
          contentTitle={movie.title}
          fetchAssignedCasts={handleFetchAssignedCasts}
          searchCasts={handleSearchCasts}
          onAssign={async (castId, role) => {
            await assignCast(castId, role);
          }}
          onRemove={async (castId) => {
            await removeCast(castId);
          }}
          onUpdateRole={async (castId, role) => {
            await updateRole(castId, role);
          }}
        />
      )}

      {/* Delete Video Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedVideoVariant(null);
        }}
        onConfirm={handleDeleteVideoConfirm}
        title="Delete Video"
        message={
          selectedVideoVariant
            ? `Are you sure you want to delete this video variant? This will permanently delete the ${selectedVideoVariant.videoType || 'video'} variant with quality ${selectedVideoVariant.quality || 'unknown'}. This action cannot be undone.`
            : 'Are you sure you want to delete this video? This action cannot be undone.'
        }
        confirmLabel="Delete Video"
        isDeleting={isDeletingVideo}
      />
    </div>
  );
};

export default MovieEditPage;

